import { Auth0Client, createAuth0Client, GetTokenSilentlyVerboseResponse, User } from '@auth0/auth0-spa-js';
import { isStorageQuotaError, pruneAuth0AndGenerationStorage, STORAGE_QUOTA_REASON } from './authStoragePrune';
import { createInvalidTokenResponseError, deriveSessionTiming } from './tokenClaims';

declare const AUTH_CLIENT_ID: string;
declare const AUTH_DOMAIN: string;
declare const ALLOWED_CONNECTION: string;
declare const AUTH_AUDIENCE: string;

const DUMMY_AUTH0_NAMESPACE = 'https://nucleai-dummy-url.com';
const DEFAULT_AUTH_SCOPE = 'openid profile email';
const DEFAULT_POST_LOGIN_RETURN_TO = '#/';
const AUTH0_MISSING_ID_TOKEN_MESSAGE = 'ID token is required but missing';

export interface Auth0LoginAppState {
  returnTo: string;
}

export interface Auth0Profile {
  labId: string;
  name: string;
  roles: string[];

  // Internal user ID once resolved from the backend; the Auth0 subject until then.
  userId: string;
  auth0UserId?: string;
  userName: string;
  userNickname: string;
  backOfficeRole: string;
}

export interface AuthSession {
  accessToken: string;
  expiresAt: number;
  issuedAt?: number;
  scope: string;
}

export interface PkceLoginResult {
  profile: Auth0Profile;
  returnTo: string;
  session: AuthSession;
}

class AuthService {
  private pkceClientPromise: Promise<Auth0Client> | null;

  private loginRedirectPromise: Promise<void> | null;

  constructor() {
    this.pkceClientPromise = null;
    this.loginRedirectPromise = null;
  }

  getPostLoginReturnTo(hash: unknown = window.location.hash): string {
    if (typeof hash !== 'string' || !hash.startsWith('#/')) {
      return DEFAULT_POST_LOGIN_RETURN_TO;
    }
    return hash === '#/login' || hash.startsWith('#/login?') ? DEFAULT_POST_LOGIN_RETURN_TO : hash;
  }

  async login() {
    if (!this.loginRedirectPromise) {
      this.loginRedirectPromise = this.startLoginRedirect().finally(() => {
        this.loginRedirectPromise = null;
      });
    }

    return this.loginRedirectPromise;
  }

  private async startLoginRedirect() {
    const client = await this.getPkceClient();
    await client.loginWithRedirect<Auth0LoginAppState>({
      appState: {
        returnTo: this.getPostLoginReturnTo(),
      },
      authorizationParams: {
        connection: ALLOWED_CONNECTION,
        redirect_uri: window.location.origin,
      },
    });
  }

  async logout() {
    const client = await this.getPkceClient();
    await client.logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });
  }

  getUserInfo = async (): Promise<Auth0Profile> => {
    const client = await this.getPkceClient();
    const user = await client.getUser();

    if (!user) {
      throw new Error('Auth0 user info could not be loaded.');
    }

    return this.getProfileFromOidcUser(user);
  };

  private getPkceClient() {
    if (!this.pkceClientPromise) {
      this.pkceClientPromise = createAuth0Client({
        domain: AUTH_DOMAIN,
        clientId: AUTH_CLIENT_ID,
        authorizationParams: {
          audience: AUTH_AUDIENCE,
          redirect_uri: window.location.origin,
          scope: DEFAULT_AUTH_SCOPE,
        },
        // Keep the rotating refresh token across reloads so renewal does not
        // depend on Auth0 iframe cookies, which browsers may block.
        cacheLocation: 'localstorage',
        useRefreshTokens: true,
        useRefreshTokensFallback: false,
      });
    }

    return this.pkceClientPromise;
  }

  private getProfileFromOidcUser(user: User): Auth0Profile {
    return {
      labId: user[`${DUMMY_AUTH0_NAMESPACE}/lab_id`] as string,
      name: user.name || '',
      roles: (user[`${DUMMY_AUTH0_NAMESPACE}/roles`] as string[]) || [],
      userId: user.sub || '',
      auth0UserId: user.sub || '',
      userName: user.name || '',
      userNickname: user.nickname || '',
      backOfficeRole: (user[`${DUMMY_AUTH0_NAMESPACE}/backoffice_role`] as string) || '',
    };
  }

  isPkceCallback() {
    const params = new URLSearchParams(window.location.search);
    return params.has('code') && params.has('state');
  }

  async completePkceLogin(): Promise<PkceLoginResult> {
    try {
      const client = await this.getPkceClient();
      // Auth0 SPA returns the persisted appState from callback processing:
      // https://auth0.github.io/auth0-spa-js/classes/Auth0Client.html#handleRedirectCallback
      const { appState } = await client.handleRedirectCallback<Auth0LoginAppState>();
      const token = await client.getTokenSilently({ detailedResponse: true });
      const user = await client.getUser();

      this.validateTokenResponse(token);

      if (!user) {
        throw new Error('Auth0 PKCE callback returned no user profile');
      }

      return {
        profile: this.getProfileFromOidcUser(user),
        returnTo: this.getPostLoginReturnTo(appState?.returnTo),
        session: this.toAuthSession(token),
      };
    } catch (error) {
      if (isStorageQuotaError(error)) {
        throw Object.assign(new Error(STORAGE_QUOTA_REASON), {
          error: STORAGE_QUOTA_REASON,
          code: STORAGE_QUOTA_REASON,
        });
      }
      throw error;
    }
  }

  pruneLeftoverAuthStorage() {
    pruneAuth0AndGenerationStorage();
  }

  async restoreCachedSession(): Promise<AuthSession | null> {
    const client = await this.getPkceClient();
    try {
      const token = await client.getTokenSilently({ detailedResponse: true, cacheMode: 'on' });
      this.validateTokenResponse(token);
      return this.toAuthSession(token);
    } catch (error) {
      if (error instanceof Error && error.message === AUTH0_MISSING_ID_TOKEN_MESSAGE) {
        throw createInvalidTokenResponseError();
      }
      throw error;
    }
  }

  async forceRefreshSession(): Promise<AuthSession> {
    const client = await this.getPkceClient();
    // Only the elected coordinator renewal may force an Auth0 exchange.
    try {
      const token = await client.getTokenSilently({ detailedResponse: true, cacheMode: 'off' });
      this.validateTokenResponse(token);
      return this.toAuthSession(token);
    } catch (error) {
      if (error instanceof Error && error.message === AUTH0_MISSING_ID_TOKEN_MESSAGE) {
        throw createInvalidTokenResponseError();
      }
      throw error;
    }
  }

  async getNewSession(): Promise<AuthSession> {
    return this.forceRefreshSession();
  }

  private toAuthSession(
    token: GetTokenSilentlyVerboseResponse & { access_token: string; expires_in: number }
  ): AuthSession {
    const timing = deriveSessionTiming(token.access_token);
    return {
      accessToken: token.access_token,
      issuedAt: timing.issuedAt,
      expiresAt: timing.expiresAt,
      scope: token.scope || DEFAULT_AUTH_SCOPE,
    };
  }

  private validateTokenResponse(
    token: GetTokenSilentlyVerboseResponse | undefined
  ): asserts token is GetTokenSilentlyVerboseResponse & { access_token: string; expires_in: number } {
    if (!token?.access_token || !Number.isFinite(token.expires_in) || token.expires_in <= 0) {
      throw createInvalidTokenResponseError();
    }
  }
}

const authService = new AuthService();
export default authService;
